import { useAuth } from "@clerk/react"
import { Download } from "lucide-react"
import { useCallback, useEffect, useMemo, useState } from "react"
import { isRateLimitError } from "../api/rateLimit"
import {
  EMPTY_HERO_LIBRARY,
  HERO_LIBRARY_STORAGE_KEY,
  HERO_LIBRARY_STORAGE_VERSION,
  HeroLibraryStateSchema,
} from "../heroes/storage"
import { useUserIdbState } from "../hooks/useIdbState"
import { useI18n } from "../i18n/useI18n"
import { isLibrarySyncUnavailable } from "../library/errors"
import {
  LibraryRefreshStatus,
  RecordOriginBadge,
} from "../library/LibrarySyncIndicators"
import { mergeAuthoritativeRemoteRecords } from "../library/merge"
import {
  useBulkDeleteDecksMutation,
  useBulkSyncDecksMutation,
  useDeleteDeckMutation,
  usePullDeckMutation,
  useSyncDeckMutation,
} from "../library/query"
import { fetchManagedDeckRecordsFromServer } from "../library/remote"
import {
  getRemoteSyncState,
  markRecordSyncFailed,
  markRecordSyncPending,
  markRecordSyncSucceeded,
  shouldAutoRetrySync,
} from "../library/syncState"
import { FilterValueButton } from "../manage/FilterValueButton"
import { ManagementFilterBar } from "../manage/ManagementFilterBar"
import type { TableFilterField } from "../manage/tableFilters"
import { useTableFilter } from "../manage/tableFilters"
import { useLibraryTransfer } from "../manage/useLibraryTransfer"
import { useSessionQuery } from "../query/authenticated"
import { queryKeys } from "../query/keys"
import type { ManagedDeckRecord } from "../schemas"
import { DeckEditorModal } from "./DeckEditorModal"
import {
  buildDuplicatedDeckRecord,
  DECK_LIBRARY_STORAGE_KEY,
  DECK_LIBRARY_STORAGE_VERSION,
  DeckLibraryStateSchema,
  EMPTY_DECK_LIBRARY,
} from "./storage"

type SortColumn = "name" | "updatedAt" | "cardCount" | "heroName"
type SortDirection = "asc" | "desc"
type SortState = { column: SortColumn; direction: SortDirection }
type DeckTableRow = {
  cardTotal: number
  heroName: string
  record: ManagedDeckRecord
}

const DEFAULT_SORT: SortState = { column: "updatedAt", direction: "desc" }

const DECK_FILTER_FIELDS: readonly TableFilterField<DeckTableRow>[] = [
  {
    key: "name",
    getValues: (row) => [row.record.deck.name],
  },
  {
    key: "id",
    aliases: ["deck"],
    getValues: (row) => [row.record.deck.deckId],
  },
  {
    key: "hero",
    getValues: (row) => [row.heroName, row.record.deck.heroId],
  },
  {
    key: "cards",
    aliases: ["count"],
    type: "number",
    getValues: (row) => [row.cardTotal],
  },
  {
    key: "entries",
    type: "number",
    getValues: (row) => [row.record.deck.mercEntries.length],
  },
  {
    key: "updated",
    getValues: (row) => [
      row.record.updatedAt,
      formatRelativeDate(row.record.updatedAt),
    ],
  },
]

function formatRelativeDate(dateString: string, locale = "en-US"): string {
  return new Intl.DateTimeFormat(locale, {
    month: "short",
    day: "numeric",
  }).format(new Date(dateString))
}

function getSortValue(row: DeckTableRow, column: SortColumn): number | string {
  switch (column) {
    case "name":
      return row.record.deck.name.toLowerCase()
    case "updatedAt":
      return row.record.updatedAt
    case "cardCount":
      return row.cardTotal
    case "heroName":
      return row.heroName.toLowerCase()
  }
}

export function DeckListPage() {
  const { locale, t } = useI18n()
  const { userId } = useAuth()
  const [decks, , setDecks] = useUserIdbState(
    DECK_LIBRARY_STORAGE_KEY,
    DeckLibraryStateSchema,
    { defaultValue: EMPTY_DECK_LIBRARY, version: DECK_LIBRARY_STORAGE_VERSION },
  )
  const [heroes] = useUserIdbState(
    HERO_LIBRARY_STORAGE_KEY,
    HeroLibraryStateSchema,
    {
      defaultValue: EMPTY_HERO_LIBRARY,
      version: HERO_LIBRARY_STORAGE_VERSION,
    },
  )

  const [sort, setSort] = useState<SortState>(DEFAULT_SORT)
  const [selectedIds, setSelectedIds] = useState<string[]>([])
  const [pendingDeleteId, setPendingDeleteId] = useState<string | null>(null)
  const [pullingDeckIds, setPullingDeckIds] = useState<string[]>([])
  const [remoteRefreshFailed, setRemoteRefreshFailed] = useState(false)
  const [remoteRefreshedAt, setRemoteRefreshedAt] = useState<string | null>(
    null,
  )
  const [syncingDeckIds, setSyncingDeckIds] = useState<string[]>([])
  const [deckEditorOpen, setDeckEditorOpen] = useState(false)
  const [editingDeck, setEditingDeck] = useState<ManagedDeckRecord | null>(null)
  const deckTransfer = useLibraryTransfer({
    getId: (record) => record.deck.deckId,
    kind: "decks",
    label: t("table.decks.title"),
    records: decks,
    setRecords: setDecks,
  })

  const heroMap = useMemo(
    () => new Map(heroes.map((r) => [r.hero.heroId, r.hero.name])),
    [heroes],
  )
  const deckRows = useMemo(
    () =>
      decks.map((record) => ({
        record,
        heroName: heroMap.get(record.deck.heroId) ?? record.deck.heroId,
        cardTotal: record.deck.mercEntries.reduce(
          (sum, entry) => sum + entry.count,
          0,
        ),
      })),
    [decks, heroMap],
  )
  const deckFilter = useTableFilter({
    records: deckRows,
    fields: DECK_FILTER_FIELDS,
  })

  const sortedDecks = useMemo(
    () =>
      [...deckFilter.filteredRecords].sort((a, b) => {
        const av = getSortValue(a, sort.column)
        const bv = getSortValue(b, sort.column)
        const cmp = av < bv ? -1 : av > bv ? 1 : 0
        return sort.direction === "asc" ? cmp : -cmp
      }),
    [deckFilter.filteredRecords, sort],
  )

  const syncDeckMutation = useSyncDeckMutation()
  const deleteDeckMutation = useDeleteDeckMutation()
  const pullDeckMutation = usePullDeckMutation()
  const bulkSyncDecksMutation = useBulkSyncDecksMutation()
  const bulkDeleteDecksMutation = useBulkDeleteDecksMutation()

  const remoteDecksQuery = useSessionQuery({
    enabled: Boolean(userId),
    queryFn: ({ sessionToken, signal }) =>
      fetchManagedDeckRecordsFromServer(sessionToken, signal),
    queryKey: queryKeys.library.decks(userId),
    refetchInterval: 60_000,
    refetchIntervalInBackground: false,
    refetchOnWindowFocus: true,
  })

  useEffect(() => {
    if (!remoteDecksQuery.data) {
      return
    }

    setDecks((previous) =>
      mergeAuthoritativeRemoteRecords(
        previous,
        remoteDecksQuery.data,
        (record) => record.deck.deckId,
      ),
    )
    setRemoteRefreshFailed(false)
    setRemoteRefreshedAt(new Date().toISOString())
  }, [remoteDecksQuery.data, setDecks])

  useEffect(() => {
    if (!remoteDecksQuery.isError) {
      return
    }
    // Keep the deck library local-first until worker sync is configured.
    setRemoteRefreshFailed(true)
  }, [remoteDecksQuery.isError])

  const hasSelection = selectedIds.length > 0
  const selectedDeckRecords = decks.filter((record) =>
    selectedIds.includes(record.deck.deckId),
  )

  const toggleSort = (column: SortColumn) => {
    setSort((prev) =>
      prev.column === column
        ? { column, direction: prev.direction === "asc" ? "desc" : "asc" }
        : {
            column,
            direction:
              column === "name" || column === "heroName" ? "asc" : "desc",
          },
    )
  }

  const sortArrow = (column: SortColumn) =>
    sort.column !== column ? null : sort.direction === "asc" ? " ^" : " v"

  const openCreateDeck = () => {
    setEditingDeck(null)
    setDeckEditorOpen(true)
  }
  const openEditDeck = (record: ManagedDeckRecord) => {
    setEditingDeck(record)
    setDeckEditorOpen(true)
  }
  const handleDeckSave = (saved: ManagedDeckRecord) => {
    setDecks((prev) => {
      const idx = prev.findIndex((r) => r.deck.deckId === saved.deck.deckId)
      if (idx >= 0) {
        const next = [...prev]
        next[idx] = saved
        return next
      }
      return [saved, ...prev]
    })
    setDeckEditorOpen(false)
    if (!userId) return

    setSyncingDeckIds((previous) => [...previous, saved.deck.deckId])
    void syncDeckMutation
      .mutateAsync(saved)
      .then((synced) => {
        setDecks((previous) => {
          const idx = previous.findIndex(
            (record) => record.deck.deckId === synced.deck.deckId,
          )
          if (idx < 0) {
            return [markRecordSyncSucceeded(synced), ...previous]
          }
          const next = [...previous]
          next[idx] = markRecordSyncSucceeded(synced)
          return next
        })
      })
      .catch((error) => {
        // Local deck saves remain valid when the worker path is unavailable.
        setDecks((previous) =>
          previous.map((entry) =>
            entry.deck.deckId !== saved.deck.deckId
              ? entry
              : isLibrarySyncUnavailable(error) || isRateLimitError(error)
                ? entry
                : markRecordSyncFailed(entry),
          ),
        )
      })
      .finally(() => {
        setSyncingDeckIds((previous) =>
          previous.filter((deckId) => deckId !== saved.deck.deckId),
        )
      })
  }
  const handleDuplicateDeck = (record: ManagedDeckRecord) => {
    const duplicated = buildDuplicatedDeckRecord(record, decks)
    setDecks((prev) => [markRecordSyncPending(duplicated), ...prev])
    if (!userId) return

    setSyncingDeckIds((previous) => [...previous, duplicated.deck.deckId])
    void syncDeckMutation
      .mutateAsync(duplicated)
      .then((synced) => {
        setDecks((previous) =>
          previous.map((entry) =>
            entry.deck.deckId === synced.deck.deckId
              ? markRecordSyncSucceeded(synced)
              : entry,
          ),
        )
      })
      .catch((error) => {
        setDecks((previous) =>
          previous.map((entry) =>
            entry.deck.deckId === duplicated.deck.deckId
              ? isLibrarySyncUnavailable(error) || isRateLimitError(error)
                ? entry
                : markRecordSyncFailed(entry)
              : entry,
          ),
        )
      })
      .finally(() => {
        setSyncingDeckIds((previous) =>
          previous.filter((deckId) => deckId !== duplicated.deck.deckId),
        )
      })
  }

  const handleSyncDeck = useCallback(
    (record: ManagedDeckRecord) => {
      if (!userId || syncingDeckIds.includes(record.deck.deckId)) return

      setSyncingDeckIds((previous) => [...previous, record.deck.deckId])
      void syncDeckMutation
        .mutateAsync(record)
        .then((synced) => {
          setDecks((previous) =>
            previous.map((entry) =>
              entry.deck.deckId === synced.deck.deckId
                ? markRecordSyncSucceeded(synced)
                : entry,
            ),
          )
        })
        .catch((error) => {
          setDecks((previous) =>
            previous.map((entry) =>
              entry.deck.deckId !== record.deck.deckId
                ? entry
                : isLibrarySyncUnavailable(error) || isRateLimitError(error)
                  ? entry
                  : markRecordSyncFailed(entry),
            ),
          )
        })
        .finally(() => {
          setSyncingDeckIds((previous) =>
            previous.filter((deckId) => deckId !== record.deck.deckId),
          )
        })
    },
    [setDecks, syncDeckMutation, syncingDeckIds, userId],
  )

  const handlePullRemoteDeck = (record: ManagedDeckRecord) => {
    if (!userId || pullingDeckIds.includes(record.deck.deckId)) return

    setPullingDeckIds((previous) => [...previous, record.deck.deckId])
    void pullDeckMutation
      .mutateAsync(record.deck.deckId)
      .then((remoteRecord) => {
        setDecks((previous) =>
          previous.map((entry) =>
            entry.deck.deckId === remoteRecord.deck.deckId
              ? markRecordSyncSucceeded(remoteRecord)
              : entry,
          ),
        )
      })
      .catch(() => {
        // Leave the local deck in conflict state if remote recovery fails.
      })
      .finally(() => {
        setPullingDeckIds((previous) =>
          previous.filter((deckId) => deckId !== record.deck.deckId),
        )
      })
  }

  const handleBulkSyncDecks = () => {
    if (!userId) return

    const recordsToSync = decks.filter(
      (record) =>
        selectedIds.includes(record.deck.deckId) &&
        !record.remoteSyncedAt &&
        !syncingDeckIds.includes(record.deck.deckId),
    )
    if (recordsToSync.length === 0) return

    setSyncingDeckIds((previous) => [
      ...previous,
      ...recordsToSync.map((record) => record.deck.deckId),
    ])

    void bulkSyncDecksMutation
      .mutateAsync(recordsToSync)
      .then(({ results }) => {
        const syncedById = new Map<string, ManagedDeckRecord>()
        const failedIds = new Set<string>()

        for (const [index, result] of results.entries()) {
          const source = recordsToSync[index]
          if (!source) continue
          if (result.status === "synced") {
            syncedById.set(
              source.deck.deckId,
              markRecordSyncSucceeded(result.record),
            )
          } else if (result.status === "conflict") {
            syncedById.set(source.deck.deckId, source)
          } else {
            failedIds.add(source.deck.deckId)
          }
        }

        setDecks((previous) =>
          previous.map((entry) => {
            const synced = syncedById.get(entry.deck.deckId)
            if (synced) return synced
            if (failedIds.has(entry.deck.deckId)) {
              return markRecordSyncFailed(entry)
            }
            return entry
          }),
        )
      })
      .catch(() => {
        // Bulk sync stays local-first when the worker sync path is unavailable.
      })
      .finally(() => {
        setSyncingDeckIds((previous) =>
          previous.filter(
            (deckId) =>
              !recordsToSync.some((record) => record.deck.deckId === deckId),
          ),
        )
      })
  }
  const handleDeleteDeck = (deckId: string) => {
    setDecks((prev) => prev.filter((r) => r.deck.deckId !== deckId))
    setSelectedIds((prev) => prev.filter((id) => id !== deckId))
    setPendingDeleteId(null)
    if (!userId) return

    void deleteDeckMutation.mutateAsync(deckId).catch(() => {
      // Deletion stays local-first when the worker path is unavailable.
    })
  }
  const handleBulkDeleteDecks = () => {
    const deletedIds = [...selectedIds]
    setDecks((prev) => prev.filter((r) => !selectedIds.includes(r.deck.deckId)))
    setSelectedIds([])
    setPendingDeleteId(null)
    if (!userId) return

    void bulkDeleteDecksMutation.mutateAsync(deletedIds).catch(() => {
      // Bulk deletion stays local-first when the worker path is unavailable.
    })
  }

  useEffect(() => {
    if (!userId) return
    if (typeof navigator !== "undefined" && navigator.onLine === false) return

    const nextRecord = decks.find(
      (record) =>
        !syncingDeckIds.includes(record.deck.deckId) &&
        shouldAutoRetrySync(record),
    )

    if (!nextRecord) return
    handleSyncDeck(markRecordSyncPending(nextRecord))
  }, [decks, handleSyncDeck, syncingDeckIds, userId])

  return (
    <section className="panel management-panel">
      <DeckEditorModal
        editingRecord={editingDeck}
        heroes={heroes}
        onClose={() => setDeckEditorOpen(false)}
        onCreateHero={() => {
          /* hero creation now lives in Heroes tab */
        }}
        onSave={handleDeckSave}
        open={deckEditorOpen}
      />

      <div className="page-header">
        <div>
          <p className="eyebrow">{t("table.decks.eyebrow")}</p>
          <h2>{t("table.decks.title")}</h2>
          <p className="lede table-lede">{t("table.decks.lede")}</p>
        </div>
        <button
          className="primary-button"
          onClick={openCreateDeck}
          type="button"
        >
          {t("table.decks.create")}
        </button>
      </div>

      <ManagementFilterBar
        filter={deckFilter}
        placeholder={t("filter.placeholder.decks")}
      />
      <p className="table-transfer-hint">{t("table.transfer.hint")}</p>

      {deckTransfer.status ? (
        <output
          className={`table-transfer-status table-transfer-status-${deckTransfer.status.tone}`}
        >
          {deckTransfer.status.message}
        </output>
      ) : null}
      <LibraryRefreshStatus
        failed={remoteRefreshFailed}
        lastRefreshedAt={remoteRefreshedAt}
      />

      <section
        aria-label={t("table.decks.title")}
        aria-busy={deckTransfer.isBusy}
        className={`table-frame${deckTransfer.isDragActive ? " table-frame-drop-active" : ""}`}
        onDragEnter={deckTransfer.onDragEnter}
        onDragLeave={deckTransfer.onDragLeave}
        onDragOver={deckTransfer.onDragOver}
        onDrop={deckTransfer.onDrop}
      >
        {deckTransfer.isDragActive ? (
          <div className="table-drop-zone">
            <strong>
              {t("table.transfer.dropTitle", { table: t("table.decks.title") })}
            </strong>
            <span>{t("table.transfer.dropBody")}</span>
          </div>
        ) : null}
        {hasSelection ? (
          <div className="bulk-bar">
            <button
              aria-label={t("table.bulk.clearSelection")}
              className="bulk-bar-dismiss"
              onClick={() => setSelectedIds([])}
              type="button"
            >
              X
            </button>
            <strong>
              {t("common.selectedCount", { count: selectedIds.length })}
            </strong>
            <div className="bulk-actions">
              <button
                className="secondary-button"
                disabled={
                  deckTransfer.isBusy || selectedDeckRecords.length === 0
                }
                onClick={() =>
                  void deckTransfer.downloadBackup(selectedDeckRecords)
                }
                type="button"
              >
                {t("table.transfer.download")}
              </button>
              <button
                className="secondary-button"
                disabled={
                  !userId ||
                  !selectedIds.some((deckId) => {
                    const record = decks.find(
                      (entry) => entry.deck.deckId === deckId,
                    )
                    return record != null && !record.remoteSyncedAt
                  })
                }
                onClick={handleBulkSyncDecks}
                type="button"
              >
                {t("table.bulk.syncSelection")}
              </button>
              <button
                className="danger-button"
                onClick={handleBulkDeleteDecks}
                type="button"
              >
                {t("common.delete")}
              </button>
            </div>
          </div>
        ) : null}
        <table className="management-table">
          <thead>
            <tr>
              <th className="sticky-column sticky-column-first" scope="col">
                <input
                  aria-label={t("table.decks.selectAll")}
                  checked={
                    sortedDecks.length > 0 &&
                    sortedDecks.every((row) =>
                      selectedIds.includes(row.record.deck.deckId),
                    )
                  }
                  onChange={() => {
                    setSelectedIds(
                      sortedDecks.every((row) =>
                        selectedIds.includes(row.record.deck.deckId),
                      )
                        ? selectedIds.filter(
                            (id) =>
                              !sortedDecks.some(
                                (row) => row.record.deck.deckId === id,
                              ),
                          )
                        : [
                            ...new Set([
                              ...selectedIds,
                              ...sortedDecks.map(
                                (row) => row.record.deck.deckId,
                              ),
                            ]),
                          ],
                    )
                  }}
                  type="checkbox"
                />
              </th>
              <th className="sticky-column sticky-column-name" scope="col">
                <button
                  className="sort-button"
                  onClick={() => toggleSort("name")}
                  type="button"
                >
                  {t("table.column.name")}
                  {sortArrow("name")}
                </button>
              </th>
              <th scope="col">
                <button
                  className="sort-button"
                  onClick={() => toggleSort("heroName")}
                  type="button"
                >
                  {t("common.hero")}
                  {sortArrow("heroName")}
                </button>
              </th>
              <th scope="col">
                <button
                  className="sort-button"
                  onClick={() => toggleSort("cardCount")}
                  type="button"
                >
                  {t("common.cards")}
                  {sortArrow("cardCount")}
                </button>
              </th>
              <th scope="col">{t("table.decks.entries")}</th>
              <th scope="col">
                <button
                  className="sort-button"
                  onClick={() => toggleSort("updatedAt")}
                  type="button"
                >
                  {t("common.updated")}
                  {sortArrow("updatedAt")}
                </button>
              </th>
              <th className="sticky-column sticky-column-last" scope="col">
                {t("table.column.actions")}
              </th>
            </tr>
          </thead>
          <tbody>
            {sortedDecks.map((row) => {
              const { record, heroName, cardTotal } = row
              const isSelected = selectedIds.includes(record.deck.deckId)
              const isPendingDelete = pendingDeleteId === record.deck.deckId
              const isPulling = pullingDeckIds.includes(record.deck.deckId)
              const isSyncing = syncingDeckIds.includes(record.deck.deckId)
              const syncState = getRemoteSyncState(record)
              const canSync = Boolean(userId) && syncState !== "synced"
              const canPullRemote = Boolean(userId) && syncState === "conflict"

              return (
                <tr data-selected={isSelected} key={record.deck.deckId}>
                  <td className="sticky-column sticky-column-first">
                    <input
                      aria-label={t("table.row.select", {
                        name: record.deck.name,
                      })}
                      checked={isSelected}
                      onChange={() => {
                        setSelectedIds((prev) =>
                          prev.includes(record.deck.deckId)
                            ? prev.filter((id) => id !== record.deck.deckId)
                            : [...prev, record.deck.deckId],
                        )
                      }}
                      type="checkbox"
                    />
                  </td>
                  <td className="sticky-column sticky-column-name">
                    <div className="identity-cell">
                      <FilterValueButton
                        filterKey="name"
                        filterValue={record.deck.name}
                        onAppendFilter={deckFilter.appendFilterValue}
                      >
                        <strong>{record.deck.name}</strong>
                      </FilterValueButton>
                      <RecordOriginBadge record={record} />
                      <FilterValueButton
                        className="table-filter-link table-filter-link-muted"
                        filterKey="id"
                        filterValue={record.deck.deckId}
                        onAppendFilter={deckFilter.appendFilterValue}
                      >
                        <small>{record.deck.deckId}</small>
                      </FilterValueButton>
                    </div>
                  </td>
                  <td>
                    <FilterValueButton
                      filterKey="hero"
                      filterValue={heroName}
                      onAppendFilter={deckFilter.appendFilterValue}
                    >
                      {heroName}
                    </FilterValueButton>
                  </td>
                  <td>
                    <FilterValueButton
                      filterKey="cards"
                      filterValue={String(cardTotal)}
                      onAppendFilter={deckFilter.appendFilterValue}
                    >
                      <span
                        className={
                          cardTotal === 24
                            ? "count-ok"
                            : cardTotal > 24
                              ? "count-over"
                              : "count-under"
                        }
                      >
                        {cardTotal} / 24
                      </span>
                    </FilterValueButton>
                  </td>
                  <td>
                    <FilterValueButton
                      filterKey="entries"
                      filterValue={String(record.deck.mercEntries.length)}
                      onAppendFilter={deckFilter.appendFilterValue}
                    >
                      {record.deck.mercEntries.length}
                    </FilterValueButton>
                  </td>
                  <td>{formatRelativeDate(record.updatedAt, locale)}</td>
                  <td className="sticky-column sticky-column-last">
                    <div className="row-actions row-actions-desktop">
                      {canSync ? (
                        <button
                          aria-label={t("table.rowAction.sync", {
                            name: record.deck.name,
                          })}
                          className="ghost-icon-button"
                          disabled={isSyncing}
                          onClick={() =>
                            handleSyncDeck(markRecordSyncPending(record))
                          }
                          type="button"
                        >
                          {t("table.rowAction.sync", {
                            name: record.deck.name,
                          })}
                        </button>
                      ) : null}
                      {canPullRemote ? (
                        <button
                          aria-label={t("table.rowAction.pullRemote", {
                            name: record.deck.name,
                          })}
                          className="ghost-icon-button"
                          disabled={isPulling}
                          onClick={() => handlePullRemoteDeck(record)}
                          type="button"
                        >
                          <Download size={16} strokeWidth={2} />
                        </button>
                      ) : null}
                      <button
                        aria-label={t("table.rowAction.edit", {
                          name: record.deck.name,
                        })}
                        className="ghost-icon-button"
                        onClick={() => openEditDeck(record)}
                        type="button"
                      >
                        {t("common.edit")}
                      </button>
                      <button
                        aria-label={t("table.rowAction.duplicate", {
                          name: record.deck.name,
                        })}
                        className="ghost-icon-button"
                        onClick={() => handleDuplicateDeck(record)}
                        type="button"
                      >
                        {t("common.copy")}
                      </button>
                      {isPendingDelete ? (
                        <span className="inline-confirm">
                          <button
                            className="danger-button"
                            onClick={() => handleDeleteDeck(record.deck.deckId)}
                            type="button"
                          >
                            {t("common.confirm")}
                          </button>
                          <button
                            className="ghost-icon-button"
                            onClick={() => setPendingDeleteId(null)}
                            type="button"
                          >
                            {t("common.cancel")}
                          </button>
                        </span>
                      ) : (
                        <button
                          aria-label={t("table.rowAction.delete", {
                            name: record.deck.name,
                          })}
                          className="ghost-icon-button danger-text"
                          onClick={() => setPendingDeleteId(record.deck.deckId)}
                          type="button"
                        >
                          {t("common.delete")}
                        </button>
                      )}
                    </div>
                    <details className="row-actions-menu">
                      <summary
                        aria-label={t("table.row.openActions", {
                          name: record.deck.name,
                        })}
                      >
                        ...
                      </summary>
                      <div className="context-menu">
                        {canSync ? (
                          <button
                            disabled={isSyncing}
                            onClick={() =>
                              handleSyncDeck(markRecordSyncPending(record))
                            }
                            type="button"
                          >
                            {t("table.rowAction.sync", {
                              name: record.deck.name,
                            })}
                          </button>
                        ) : null}
                        <button
                          onClick={() => openEditDeck(record)}
                          type="button"
                        >
                          {t("common.edit")}
                        </button>
                        <button
                          onClick={() => handleDuplicateDeck(record)}
                          type="button"
                        >
                          {t("common.duplicate")}
                        </button>
                        <button
                          onClick={() => handleDeleteDeck(record.deck.deckId)}
                          type="button"
                        >
                          {t("common.delete")}
                        </button>
                      </div>
                    </details>
                  </td>
                </tr>
              )
            })}
          </tbody>
        </table>
      </section>
    </section>
  )
}
